import Exporter from '../exporter/Exporter.js'
import { DesignPackage } from './DesignPackage.js'
import { getAdapter, getPackageableAdapters } from '../stores/resourceRegistry.js'

/**
 * Builds a DesignPackage from the live editor session — the one place that
 * turns "current app state" into design.json + manifest.json + resource
 * folders (see docs plan "Design Packages"). Nothing else assembles a
 * package; ZIP/Git/IndexedDB/Tauri all just persist what this returns.
 *
 * @param {object} params
 * @param {object} params.settings         - appSettings (plate/theme, passed to Exporter)
 * @param {object} params.layer            - the live Konva layer
 * @param {string} params.designId         - stable package identity (manifest.id — see plan "Package identity")
 * @param {string} params.name
 * @param {string} [params.description]
 * @param {string} [params.author]
 * @param {array}  [params.versionHistory]
 * @param {array}  [params.guidelines]
 * @param {object} [params.bookmarks]
 * @param {array}  [params.datasets]
 * @param {object|null} [params.previewNode] - a Konva node with .toDataURL(), or null to skip preview
 * @param {boolean} [params.generatePreview=true] - regenerate preview.png (default); false keeps whatever existingPackage already had
 * @param {DesignPackage|null} [params.existingPackage] - previous package being updated in place (preserves unrecognized files/manifest keys)
 * @returns {Promise<DesignPackage>}
 */
export async function buildDesignPackage({
  settings, layer, designId, name,
  existingPackage = null,
  // Fall back to whatever the package already had rather than blanking it —
  // manifest.json is regenerated every save, but that must never mean
  // silently discarding metadata the caller didn't intend to change.
  description = existingPackage?.manifest?.description ?? '',
  author = existingPackage?.manifest?.author ?? '',
  versionHistory = [], guidelines = [], bookmarks = [], datasets = [],
  previewNode = null, generatePreview = true,
}) {
  const pkg = new DesignPackage(
    existingPackage ? { ...existingPackage.manifest } : {},
    existingPackage ? new Map(existingPackage.files) : new Map()
  )

  // 1. design.json — a plain JSON document (see DesignPackage.setDesignJson).
  // includeHass=true: design.json is this app's own full-fidelity round-trip
  // format, not the firmware plate export — hass_config (HA entity bindings/
  // automation) is designer-authored data that must survive a save/reopen
  // just like any other widget property (see ObjectExport.export()'s
  // `includeHass` gate). Passing false here would silently drop it.
  const exporter = new Exporter()
  const objects = exporter.doExport(settings, layer, true, true)
  const audioAdapter = getAdapter('audio')
  const audioStreams = audioAdapter?.serializeExternalResources?.(layer) ?? []
  const audioSynths = audioAdapter?.serializeSynthReferences?.(layer) ?? []
  pkg.setDesignJson({ id: designId, versionHistory, guidelines, bookmarks, datasets, audioStreams, audioSynths, objects })

  // 2. Resource folders. The package layer never mentions images/fonts by
  // name — it only iterates whatever adapters declare a packageFolder, and
  // each adapter owns how its own resources are serialized (see
  // imageResourceAdapter.js/fontResourceAdapter.js's toPackageFile()).
  for (const adapter of getPackageableAdapters()) {
    const prefix = `${adapter.packageFolder}/`
    // Rebuilt fully from what's actually used right now — no stale/orphaned
    // resource files left over from a previous save.
    for (const path of [...pkg.files.keys()]) {
      if (path.startsWith(prefix)) pkg.deleteFile(path)
    }
    const used = adapter.list().filter(r => adapter.isUsed(r.uuid, layer))
    for (const resource of used) {
      const file = await adapter.toPackageFile(resource.uuid)
      if (!file) continue
      pkg.setFile(prefix + file.filename, {
        content: file.content,
        encoding: file.encoding,
        mime: file.mime,
      })
    }
  }

  // 3. manifest.json — always regenerated from live session state, never
  // hand-maintained (see plan "Package identity"). Unknown keys preserved.
  pkg.regenerateManifest({ id: designId, name, description, author })

  // 4. preview.png — always regenerated unless the caller explicitly opts out.
  if (generatePreview && previewNode?.toDataURL) {
    const dataUrl = previewNode.toDataURL()
    const match = /^data:([^;]+);base64,(.*)$/s.exec(dataUrl)
    if (match) {
      pkg.setFile('preview.png', { content: match[2], encoding: 'base64', mime: match[1] })
    }
  }

  return pkg
}
